import { createContext, useContext } from "react";

import type {
  OpenDrawerFn,
  CloseDrawerFn,
  NavigationVariants,
} from "@ui/components/navigation/Navigation/Navigation";
import { type Campaign } from "@voxmedia/duet-context/context/campaign-context";

export type PageRefCallbackType = (node: HTMLDivElement | null) => void;
type Unsubscribe = () => void;
type AddSubscriberType = (callback: PageRefCallbackType) => Unsubscribe;

/**
 * Navigation overrides for boosted posts
 * All overrides can be set to "none" to reset to original values
 */
export type NavigationOverrides = {
  /** Override the variant for the navigation */
  variant?: NavigationVariants | "none";
  /** Override the headline for the navigation */
  headline?: string;
  /** Override the campaign for the navigation (for branded posts) */
  campaign?: Campaign | "none";
};

export type NavigationContextType = {
  /** Sticky nav className to be used inside of <article> that informs when to show the Sticky Nav */
  stickyNavTriggerClassName: string;

  /** Callback to set reference to the <article> that informs the boundary of the Sticky Nav */
  articleRef: PageRefCallbackType;

  /** Subscribe and notify about changes to <article> ref */
  addArticleRefSubscriber: AddSubscriberType;

  /** Call to open the drawer */
  openDrawer: OpenDrawerFn;

  /** Use this to set the callback for opening the drawer */
  setOpenDrawerFn: (fn: OpenDrawerFn) => void;

  /** Call to open the drawer */
  closeDrawer: CloseDrawerFn;

  /** Use this to set the callback for closing the drawer */
  setCloseDrawerFn: (fn: CloseDrawerFn) => void;

  /** Drawer id used in `aria-controls` */
  drawerId: string;

  /** Navigation overrides (for boosted posts) */
  navigationOverrides?: NavigationOverrides;

  /**
   * Set navigation overrides for boosted posts
   */
  setNavigationOverrides: (overrides?: NavigationOverrides) => void;

  /** Use this to set the callback for when the comment count is clicked */
  setCommentCountClickFn: (fn: () => void) => void;

  /** Callback to be called when the comment count is clicked */
  onCommentCountClick: () => void;
};

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useNavigationContext = (): NavigationContextType => {
  const navigationContext = useContext(NavigationContext);

  if (!navigationContext) {
    throw new Error("Missing Navigation Context");
  }

  return navigationContext;
};

export default NavigationContext;
